import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { ApiOperationType, EntityIdentificationType } from '@ydistri/api-sdk';
import {
  EntityListProductInputData,
  EntityListTypeSupported,
  getEntityListSpecification,
  IMPORT_STEPS,
} from '../entityListsLib';
import { ReduxState, useAppDispatch } from '../../../../store';
import {
  initImport,
  setImportedEntityListItems,
  setImportStepRunning,
} from '../entityListAdministrationSlice';
import {
  useCreateEntityListImportMutation,
  usePutEntityListImportItemsMutation,
} from '../apiEntityLists';
import { apiSlice, APITAGS } from '../../../../apis/api';
import { getRequestErrorMessage } from '../../../../lib/utils/getRequestErrorMessage';
import { addToast } from '@ydistri/utils';
import { useSetImportStep } from './useSetImportStep';
import { useSetImportStepRunning } from './useSetImportStepRunning';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { useCurrentEntityListState } from './useCurrentEntityListState';
import { ImportedItemRow } from '../../../../lib/imports/importTypes';
import useEntityListImportFormat from './useEntityListImportFormat';
import { selectImportableItemParser } from '../../../../lib/imports/importLib';
import useImportedItemValidatorContext from './useImportedItemValidatorContext';

export type ImportItemFunction = (apiOperationType?: ApiOperationType) => void;

export interface UseImportItemsData {
  isPending: boolean;
  callback: ImportItemFunction;
}

const useImportItems = (entityListType: EntityListTypeSupported): UseImportItemsData => {
  const dispatch = useAppDispatch();
  const setStep = useSetImportStep(entityListType);
  const setStepStatus = useSetImportStepRunning(entityListType);

  const specification = getEntityListSpecification(entityListType);

  const selectedEntityList = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].selectedEntityList,
  );

  const importsMap = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].imports,
  );

  const entityListState = useCurrentEntityListState(entityListType);
  const inputFormat = useEntityListImportFormat(entityListType);

  const storeIdentificationType =
    entityListState?.storeIdentificationType ?? EntityIdentificationType.CustomerId;
  const productIdentificationType =
    entityListState?.productIdentificationType ?? EntityIdentificationType.CustomerId;
  const productItemsContent = entityListState?.productItemsContent ?? '';

  const [createEntityListImport, createEntityListImportStatus] =
    useCreateEntityListImportMutation();
  const [putEntityListImportItems, putEntityListImportItemsStatus] =
    usePutEntityListImportItemsMutation();

  const validatorContext = useImportedItemValidatorContext(
    productIdentificationType,
    storeIdentificationType,
  );

  const insertImportItems = useCallback(
    (
      entityListId: number,
      verificationTimeStamp: string,
      parsedRows: readonly ImportedItemRow<EntityListProductInputData>[],
    ) => {
      const entityListImpl = getEntityListSpecification(entityListType);

      //we only want to send valid items
      const validParsedRows = parsedRows.filter(row => !row.isError);
      const itemsWithHeaders = entityListImpl.convertToStructuredText(validParsedRows);

      putEntityListImportItems({
        entityListId: entityListId,
        importItemsEncodedBase64: btoa(itemsWithHeaders),
        verificationTimeStamp,
      })
        .unwrap()
        .then(() => {
          setStepStatus(IMPORT_STEPS.ITEM_VALIDATION, false);
          setStep(IMPORT_STEPS.ITEM_VALIDATION);

          dispatch(
            apiSlice.util.invalidateTags([
              APITAGS.entityListsAdministration.entityListImportSummary,
            ]),
          );
          dispatch(
            apiSlice.util.invalidateTags([APITAGS.entityListsAdministration.entityListImportItems]),
          );
        })
        // An upload the browser aborted has no response to read the messages from.
        .catch((error: unknown) => {
          dispatch(
            addToast({
              message: `Import failed: ${getRequestErrorMessage(error, 'the request failed')}`,
              isError: true,
            }),
          );
        })
        .finally(() => {
          dispatch(
            setImportStepRunning({
              entityListType,
              data: {
                step: IMPORT_STEPS.ITEM_VALIDATION,
                value: false,
              },
            }),
          );
        });
    },
    [dispatch, entityListType, putEntityListImportItems, setStep, setStepStatus],
  );

  const callback = useCallback(
    (apiOperationType: ApiOperationType | undefined = ApiOperationType.Replace) => {
      if (productItemsContent.length === 0) {
        return;
      }

      const parser = selectImportableItemParser<EntityListProductInputData>(['text/plain']);
      if (!parser) {
        return;
      }

      const parsedRows = parser.parse(
        productItemsContent,
        inputFormat,
        specification.getValidators(validatorContext),
      );
      if (!(parsedRows.length > 0 && selectedEntityList)) {
        return;
      }

      setStepStatus(IMPORT_STEPS.ITEM_VALIDATION, true);

      // dispatch(setIsLoading(true));
      dispatch(setImportedEntityListItems({ entityListType, data: parsedRows }));

      const currentTimestamp = importsMap[selectedEntityList.entityListId].verificationTimeStamp;

      if (!currentTimestamp) {
        // no import has been created yet
        const verificationTimeStamp = new Date().toISOString();

        createEntityListImport({
          entityListType,
          entityListId: selectedEntityList.entityListId,
          apiOperationType: apiOperationType,
          verificationTimeStamp,
          productIdentificationTypeId: productIdentificationType,
          storeIdentificationTypeId: storeIdentificationType,
        })
          .unwrap()
          .then(() => {
            dispatch(
              initImport({ entityListType, importType: apiOperationType, verificationTimeStamp }),
            );
            insertImportItems(selectedEntityList.entityListId, verificationTimeStamp, parsedRows);
          })
          .catch((error: unknown) => {
            dispatch(
              addToast({
                message: `Import failed: ${getRequestErrorMessage(error, 'the request failed')}`,
                isError: true,
              }),
            );
            setStepStatus(IMPORT_STEPS.ITEM_VALIDATION, false);
          });
      } else {
        // we already have an import, only insert the items
        insertImportItems(selectedEntityList.entityListId, currentTimestamp, parsedRows);
      }
    },
    [
      createEntityListImport,
      dispatch,
      entityListType,
      importsMap,
      inputFormat,
      insertImportItems,
      productIdentificationType,
      productItemsContent,
      selectedEntityList,
      setStepStatus,
      specification,
      storeIdentificationType,
      validatorContext,
    ],
  );

  const isPending =
    putEntityListImportItemsStatus.status === QueryStatus.pending ||
    createEntityListImportStatus.status === QueryStatus.pending;

  return useMemo(
    () => ({
      isPending,
      callback,
    }),
    [isPending, callback],
  );
};

export default useImportItems;
